import { useContext, useMemo } from 'react';
import { useKnowledgeNodes } from '@/entities/knowledge';
import { getKmEntityTitle, KM_ENTITY_TYPES, useKmEntities, useSearchKmEntities } from '@/entities/ontology';
import { ExpertReportModelModeContext } from './expertReportModelModeContext';

/** Знание, предлагаемое к подстановке в комментарий. */
export type ReportKnowledgeSuggestion = {
  id: number;
  name: string;
};

/** Сколько знаний показывается в списке подсказок */
const SUGGESTIONS_LIMIT = 20;

/** Пустой список — общая ссылка, чтобы состав подсказок не менялся от одного рендера к другому */
const NO_SUGGESTIONS: ReportKnowledgeSuggestion[] = [];

/**
 * Знания, предлагаемые к подстановке в комментарий результата: у заключения на модели знаний —
 * ноды модели, у прежнего — ноды прежней базы знаний. Подставленная ссылка остаётся рабочей и
 * после сохранения только тогда, когда знание взято из той же модели, по которой посчитано
 * заключение, — иначе бэкенд снимет её как ссылку на незнакомое знание.
 *
 * Пустой запрос оставляет список без сужения: у модели знаний перечень даёт список её нод, у
 * прежней базы — её собственный список.
 */
export const useReportKnowledgeSuggestions = (query: string): ReportKnowledgeSuggestion[] => {
  const modelMode = useContext(ExpertReportModelModeContext);
  const isKmMode = modelMode === 'km';
  const search = query || undefined;

  const legacyNodes = useKnowledgeNodes({ limit: SUGGESTIONS_LIMIT, isDeleted: false, search }, { enabled: !isKmMode });
  // Полнотекстовый поиск нод модели знаний ведётся отдельным методом, поэтому список нод
  // остаётся источником только для пустого запроса
  const kmNotes = useKmEntities(
    { type: [KM_ENTITY_TYPES.NOTE], includeDeleted: false, limit: SUGGESTIONS_LIMIT },
    { enabled: isKmMode && !search },
  );
  const kmFoundNotes = useSearchKmEntities(
    { query, type: [KM_ENTITY_TYPES.NOTE], limit: SUGGESTIONS_LIMIT },
    { enabled: isKmMode },
  );

  const kmItems = search ? kmFoundNotes.data?.items : kmNotes.data?.items;

  return useMemo(() => {
    if (isKmMode) {
      return kmItems?.map((entity) => ({ id: entity.id, name: getKmEntityTitle(entity) })) ?? NO_SUGGESTIONS;
    }
    return legacyNodes.data?.knowledgeNodes.map((node) => ({ id: node.id, name: node.name })) ?? NO_SUGGESTIONS;
  }, [isKmMode, kmItems, legacyNodes.data]);
};
